import { Dispatch, ReactNode, SetStateAction } from 'react';
import SelectInventory from './steps/SelectInventory';
import SelectPool from './steps/SelectPool';
import SetRange from './steps/SetRange';
import Review from './steps/Review';
import Processing from './steps/Processing';
import Success from './steps/Success';
import { CreateLiquidityPositionDetails, Step } from './types';
import { Address } from 'viem';
import { useTranslations } from 'use-intl';
import { Asset } from 'nftx.js';
import { QueryStatus } from '@tanstack/react-query';
import Link from 'next/link';

export interface CreateLiquidityPositionProps {
  step: Step;
  setStep: Dispatch<SetStateAction<Step>>;
  collection: Address;
  collectionName: string;
  state: CreateLiquidityPositionDetails;
  dispatch: Dispatch<Partial<CreateLiquidityPositionDetails>>;
  symbol: string;
  vaultId?: string;
  icon?: string;
  userAssets?: Asset[];
  isNewVault?: boolean;
  isNewPool: boolean;
  liquidity: bigint;
  poolAddress: Address;
  tokenAddress: Address;
  currentApy: bigint;
  ethMax: bigint;
  currentNFTPriceInETH: bigint;
  currentTick: bigint;
  userAddress: Address | undefined;
  standard: 'ERC721' | 'ERC1155';
  amountMax: bigint;
  withinModal?: boolean;
  settings: ReactNode;
  status: QueryStatus;
  previousUrl: string;
}

const CreateLiquidityPosition = ({
  step,
  setStep,
  state,
  dispatch,
  symbol,
  vaultId,
  icon,
  collection,
  collectionName,
  userAssets,
  isNewVault = false,
  isNewPool,
  liquidity,
  poolAddress,
  tokenAddress,
  currentApy,
  currentNFTPriceInETH,
  currentTick,
  ethMax,
  userAddress,
  standard,
  amountMax,
  withinModal,
  settings,
  status,
  previousUrl,
}: CreateLiquidityPositionProps) => {
  const t = useTranslations('CreateLiquidityPosition');
  const steps = (
    state.poolType === 'eth' || state.using === 'vtoken'
      ? ['pool', 'range', 'review', 'processing', 'success']
      : ['pool', 'inventory', 'range', 'review', 'processing', 'success']
  ) as Step[];

  const previousStep = steps[steps.findIndex((x) => x === step) - 1];
  const nextStep = steps[steps.findIndex((x) => x === step) + 1];

  return (
    <div>
      <header className="bg-gray-200 px-4 py-3 font-medium text-gray-500 flex items-center justify-between relative">
        <div>{t('title')}</div>
        <div className={`${withinModal ? 'mr-6' : ''}`}>{settings}</div>
      </header>
      <div
        className={`grid gap-1 ${
          steps.length === 5 ? 'grid-cols-5' : 'grid-cols-4'
        } h-1 w-full`}
      >
        {steps.map((x, i) => (
          <span
            className={`${x === step ? 'bg-pink-500' : 'bg-gray-300'}`}
            key={i}
          />
        ))}
      </div>
      <section className="bg-gray-50 px-4 md:px-6 pb-6 pt-4">
        <aside className="pb-4">
          {previousStep ? (
            <button
              type="button"
              className="text-pink-500 text-xs hover:underline"
              onClick={() =>
                setStep((currentStep) => previousStep || currentStep)
              }
            >
              {t('back')}
            </button>
          ) : previousUrl ? (
            <Link
              href={previousUrl}
              className="text-pink-500 text-xs hover:underline"
            >
              {t('back')}
            </Link>
          ) : null}
        </aside>
        {step === 'pool' && (
          <SelectPool
            isNewVault={isNewVault}
            feeTier={state.feeTier}
            poolType={state.poolType}
            using={state.using}
            symbol={symbol}
            vaultId={vaultId}
            icon={icon}
            collection={collection}
            collectionName={collectionName}
            dispatch={dispatch}
            setStep={setStep}
            onSubmit={() => setStep(nextStep)}
          />
        )}
        {step === 'inventory' && (
          <SelectInventory
            status={status}
            items={state.items}
            collection={collection}
            dispatch={dispatch}
            onSubmit={() => setStep(nextStep)}
            withinModal={withinModal}
            symbol={symbol}
            collectionName={collectionName}
            icon={icon}
            vaultId={vaultId}
            userAssets={userAssets}
            is1155={standard === 'ERC1155'}
          />
        )}
        {step === 'range' && (
          <SetRange
            symbol={symbol}
            icon={icon}
            vaultId={vaultId}
            onSubmit={() => setStep(nextStep)}
            dispatch={dispatch}
            isNewVault={isNewVault}
            isNewPool={isNewPool}
            liquidity={liquidity}
            poolAddress={poolAddress}
            tokenAddress={tokenAddress}
            state={state}
            currentApy={currentApy}
            currentNFTPriceInETH={currentNFTPriceInETH}
            currentTick={currentTick}
            ethMax={ethMax}
            amountMax={amountMax}
            using={state.using}
            collectionAddress={collection}
          />
        )}
        {step === 'review' && (
          <Review
            state={state}
            symbol={symbol}
            collectionName={collectionName}
            setStep={setStep}
            using={state.using}
          />
        )}
        {step === 'processing' && (
          <Processing
            state={state}
            symbol={symbol}
            userAddress={userAddress}
            vaultId={vaultId || ''}
            poolId={poolAddress}
            onCancel={() => setStep(previousStep)}
            onComplete={() => setStep('success')}
          />
        )}
        {step === 'success' && <Success vaultId={vaultId || ''} />}
      </section>
      <footer className="px-6 py-3 text-xs text-gray-400 font-semibold flex justify-between bg-gray-200">
        <p>{'You are creating a Liquidity Position'}</p>
        <a
          href="https://docs.nftx.io/v/nftx-v3/"
          className="underline hover:no-underline"
          target="_blank"
          rel="nofollow noreferrer"
        >
          {'Help'}
        </a>
      </footer>
    </div>
  );
};

export default CreateLiquidityPosition;
